/**
 * OpenTelemetry configuration for browser error tracking and tracing.
 * Provides centralized error reporting with PII redaction.
 * Sends traces to Grafana Cloud via OTLP/HTTP.
 */

import { SpanStatusCode, trace } from "@opentelemetry/api";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BatchSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { SEMRESATTRS_DEPLOYMENT_ENVIRONMENT, SEMRESATTRS_SERVICE_NAME, SEMRESATTRS_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";

/**
 * Fields to redact from telemetry events to prevent PII/sensitive data exposure.
 */
const SENSITIVE_FIELDS = [
  "password",
  "token",
  "api_key",
  "apikey",
  "secret",
  "authorization",
  "cookie",
  "session",
  "credit_card",
  "ssn",
  "email",
  "phone",
  "address",
];

/**
 * Redact sensitive data from an object recursively.
 */
function redactSensitiveData(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === "string") {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(obj)) {
      return "[REDACTED_EMAIL]";
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(redactSensitiveData);
  }

  if (typeof obj === "object") {
    const redacted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const keyLower = key.toLowerCase();
      if (SENSITIVE_FIELDS.some((field) => keyLower.includes(field))) {
        redacted[key] = "[REDACTED]";
      } else {
        redacted[key] = redactSensitiveData(value);
      }
    }
    return redacted;
  }

  return obj;
}

let _initialized = false;
let _tracer: ReturnType<typeof trace.getTracer> | null = null;

/**
 * Initialize OpenTelemetry with configuration.
 * Should be called once at application startup.
 */
export function initOTel(): void {
  // Browser sends traces to the same-origin /otlp/v1/traces path.
  // In dev, Vite proxies this to Grafana Cloud (see vite.config.ts).
  // In prod, nginx proxies this to Grafana Cloud (see nginx.conf).
  // Auth headers are kept server-side — never exposed to the browser.
  const endpoint = "/otlp/v1/traces";

  if (_initialized) {
    return;
  }
  _initialized = true;

  const resource = resourceFromAttributes({
    [SEMRESATTRS_SERVICE_NAME]: "web",
    [SEMRESATTRS_SERVICE_VERSION]: import.meta.env.VITE_APP_VERSION || "0.0.0",
    [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]:
      import.meta.env.VITE_OTEL_ENVIRONMENT || "development",
  });

  const exporter = new OTLPTraceExporter({
    url: endpoint,
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  provider.register();

  _tracer = trace.getTracer("web");

  // Auto-instrument fetch/XHR and user interactions
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [/.*/],
        applyCustomAttributesOnSpan: (span, request) => {
          try {
            const url = typeof request === "string" ? request : "url" in request ? request.url : "";
            span.setAttribute("http.url", redactSensitiveData(url) as string);
          } catch (e) {
            console.debug("OTel fetch attribute error", e);
          }
        },
      }),
      new UserInteractionInstrumentation(),
    ],
  });
}

/**
 * Set user context for telemetry events.
 * Call this when user logs in.
 */
export function setOTelUser(user: {
  id?: string;
  email?: string;
  username?: string;
}): void {
  // OTel doesn't have a global user scope like Sentry.
  // We store it on window for span enrichment and use baggage.
  try {
    const redacted = redactSensitiveData(user) as typeof user;
    (window as unknown as Record<string, unknown>).__otelUser = redacted;
  } catch (e) {
    console.debug("OTel setOTelUser error", e);
  }
}

/**
 * Clear user context on logout.
 */
export function clearOTelUser(): void {
  try {
    delete (window as unknown as Record<string, unknown>).__otelUser;
  } catch (e) {
    console.debug("OTel clearOTelUser error", e);
  }
}

/**
 * Capture an exception manually.
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (!_tracer) return;
  try {
    const span = _tracer.startSpan("exception", {
      attributes: {
        "exception.type": error.name || "Error",
        "exception.message": error.message,
      },
    });
    if (error.stack) {
      span.setAttribute("exception.stacktrace", error.stack);
    }
    if (context) {
      const redacted = redactSensitiveData(context) as Record<string, unknown>;
      for (const [key, value] of Object.entries(redacted)) {
        span.setAttribute(`custom.${key}`, String(value));
      }
    }
    span.recordException(error);
    span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
    span.end();
  } catch (e) {
    console.debug("OTel captureException error", e);
  }
}

/**
 * Capture a message manually.
 */
export function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info"
): void {
  if (!_tracer) return;
  try {
    const span = _tracer.startSpan("message");
    span.setAttribute("message.text", message);
    span.setAttribute("message.level", level);
    if (level === "error") {
      span.setStatus({ code: SpanStatusCode.ERROR, message });
    }
    span.end();
  } catch (e) {
    console.debug("OTel captureMessage error", e);
  }
}

/**
 * Add a breadcrumb for debugging context.
 * OTel doesn't have breadcrumbs natively — we emit a lightweight span event.
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  data?: Record<string, unknown>
): void {
  try {
    const activeSpan = trace.getActiveSpan();
    if (activeSpan) {
      activeSpan.addEvent("breadcrumb", {
        "breadcrumb.message": message,
        "breadcrumb.category": category || "default",
        ...(data ? redactSensitiveData(data) as Record<string, unknown> : {}),
      });
    }
  } catch (e) {
    console.debug("OTel addBreadcrumb error", e);
  }
}

export { trace };
